/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { IconAlertWarningSolid, IconCalendar } from '@hulu-react-style-components/icons';
import envConfig from '@hulu/env-config';
import { capitalize as _capitalize } from 'lodash';
import * as React from 'react';
import type { Cell } from 'react-table';

import type { LineItemSsp } from '../../../../apis/graphql';
import DateHeaderLabel from '../../../../common/DateHeaderLabel/DateHeaderLabel';
import { getTimezoneWithGMT } from '../../../../common/SelectTimeZone/utils';
import { resizingData, stickyBlock } from '../../../../common/TraffickingTable/utils';
import { pacingTypes } from '../../../../configs';
import { TraffickingTableName } from '../../../../constants';
import type { Nullable } from '../../../../models';
import bem from '../../../../utils/bem';
import { formatDecimalValuePercent } from '../../../../utils/formatting';
import { DEFAULT_COLUMN_MIN_WIDTH, DELIVERY_FORENSICS_LINK } from '../../constants';
import {
  LineItemColumn,
  LineItemColumnMeta,
  LineItemPacingStatusAlignmentColumnsMeta,
} from '../../constants/lineItems';
import {
  getAlertMessage,
  getCreatedAt,
  getDisplayPublisherTarget,
  getDistributionPartners,
  getFrequencyCapList,
  getTrackingId,
  getUpdatedAt,
} from '../../ExportDropdown/utils/commonAccessors';
import { getCurrencyValueToShow, getPercentageValueToShow } from '../../ExportDropdown/utils/utils';
import {
  AlertCell,
  BufferCell,
  BufferOptInCell,
  CreateAndEditAdCell,
  CustomPacingRequestedCell,
  DealIdCell,
  DeliveryForensicsCell,
  LineItemFrequencyCapCell,
  LineItemStatusCell,
  NameCell,
  PacingStatusCell,
  PacingTypeCell,
  PercentCell,
  PriorityNameCell,
  ReviewCell,
  TierNameCell,
} from '../cells';
import type { AlertFlagProps } from '../cells/AlertCell';
import { AlertFiled } from '../cells/AlertCell/constants';
import CountryCell from '../cells/CountryCell';
import { getUniqueCountriesString } from '../cells/CountryCell/utils';
import CustomPacingAppliedCell from '../cells/CustomPacingAppliedCell';
import InventoryBlockCell from '../cells/InventoryBlockCell';
import { cellContentsByPacingStatus } from '../cells/PacingStatusCell/constants';
import YodaPacingStatusCell from '../cells/YodaPacingStatusCell';
import { EntityType } from '../hooks/makePageDrawerPlugin';
import type { LineItemModel } from '../modelConverters';
import { CellType } from '../types';
import type { CustomColumnConfig } from './utils';
import {
  ABSENT_DISPLAY,
  ABSENT_EXPORT,
  boolToYes,
  formatMicroCurrencyAmount,
  formatStartEndDateString,
  formatUTCToTimezone,
  getAccountRelatedColumnAccessor,
  getDistributionPartnerList,
  getIdFromTableCell,
  getLineItemRemainingExpectedCompletionsAcc,
  getLineItemThreshold,
  getMetricsPercentRelatedColumnAccessor,
  getMetricsRelatedColumnAccessor,
  getPercentageValueForExport,
  getPublisherTargetingList,
  getStaqErrorFailureCodes,
  getStaqErrorMessage,
  numberToCurrencyAmount,
  numberToPercentage,
  stringToDateTimeString,
} from './utils';

const [, element] = bem('alert-cell');

export const getLineItemsAlertFlags = ({
  id,
  hasMisalignedSchedule,
  hasZeroDeliveryRisk,
  exclusiveBlock,
  staqError,
  staqErrorExtended,
  endDate,
  schedule,
}: LineItemModel): AlertFlagProps[] => {
  const staqErrorfailureCodes = getStaqErrorFailureCodes(staqError, staqErrorExtended, endDate, schedule?.timezone);
  const staqErrorMessage = getStaqErrorMessage(staqErrorfailureCodes);

  return [
    {
      id,
      field: AlertFiled.ZeroDelivery,
      isShown: !!hasZeroDeliveryRisk,
      hoverMessage: 'This line item has not delivered any impressions in the last 12 hours.',
    },
    {
      id,
      field: AlertFiled.MisalignedSchedule,
      isShown: !!hasMisalignedSchedule,
      icon: <IconCalendar width={18} height={18} />,
      hoverMessage: 'This line item has ads with a misaligned schedule.',
      className: element('red-icon'),
    },
    {
      id,
      field: AlertFiled.InventoryBlock,
      icon: <InventoryBlockCell exclusiveBlock={exclusiveBlock} />,
      isShown: !!exclusiveBlock,
      hoverMessage: 'This line item has an inventory block.',
      className: element('red-icon', 'clickable'),
    },
    {
      id,
      field: AlertFiled.StaqError,
      isShown: !!staqErrorMessage,
      icon: <IconAlertWarningSolid width={18} height={18} />,
      hoverMessage: staqErrorMessage,
      className: element('red-icon'),
    },
  ];
};

const commonBooleanCellConfig = (id: string) => ({
  Cell: ({ value, isNoShown }: { value: Nullable<boolean>; isNoShown?: boolean }) => (
    <span>{boolToYes({ value, isNoShown })}</span>
  ),
  width: resizingData.getData(190, TraffickingTableName.lineItems, id),
  minWidth: DEFAULT_COLUMN_MIN_WIDTH,
});

type GetLineItemsColumnsProps = {
  disabledColumns?: typeof LineItemColumn[keyof typeof LineItemColumn][];
  adsPacingStatusAlignment?: boolean;
  adsPacingStatusAlignmentDefaultHide?: boolean;
};

// NOTE: `Cell` property cannot return `undefined` or the page will crash
const getLineItemsColumns = ({
  disabledColumns,
  adsPacingStatusAlignment = false,
  adsPacingStatusAlignmentDefaultHide = false,
}: GetLineItemsColumnsProps = {}): CustomColumnConfig<LineItemModel>[] => {
  const getLineItemsColumnMeta = (columnName: string): { header?: string; tooltip?: string; subHeader?: string } => {
    if (adsPacingStatusAlignment) {
      return LineItemPacingStatusAlignmentColumnsMeta[columnName] ?? LineItemColumnMeta[columnName];
    }

    return LineItemColumnMeta[columnName];
  };

  const columns: CustomColumnConfig<LineItemModel>[] = [
    {
      id: LineItemColumn.Alert,
      Header: LineItemColumnMeta[LineItemColumn.Alert].header,
      Cell: (cell: Cell<LineItemModel>) => (
        <AlertCell flags={getLineItemsAlertFlags(cell.row.original)} doNotTraffic={cell.row.original.doNotTraffic} />
      ),
      width: resizingData.getData(90, TraffickingTableName.lineItems, 'Alert'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.Alert].tooltip,
      exportAccessor: (li) =>
        getAlertMessage({
          hasZeroDeliveryRisk: !!li.hasZeroDeliveryRisk,
          hasMisalignedSchedule: !!li.hasMisalignedSchedule,
          hasLineItemExclusiveBlock: !!li.inventoryBlockId,
          staqErrorMessage: getStaqErrorMessage(
            getStaqErrorFailureCodes(li.staqError, li.staqErrorExtended, li.endDate, li.schedule?.timezone),
            ', ',
            true
          ),
        }),
    },
    {
      id: LineItemColumn.Name,
      accessor: 'name',
      Header: LineItemColumnMeta[LineItemColumn.Name].header,
      Cell: ({ row: { original }, openTraffickingPageDrawer }) => (
        <NameCell
          rowOriginal={original}
          entityType={EntityType.LINE_ITEM}
          openTraffickingPageDrawer={openTraffickingPageDrawer}
        />
      ),
      disableHiding: true,
      width: resizingData.getData(360, TraffickingTableName.lineItems, 'Name'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      className: stickyBlock('name'),
      tooltipMessage: LineItemColumnMeta[LineItemColumn.Name].tooltip,
    },
    {
      id: LineItemColumn.CreateAd,
      Header: LineItemColumnMeta[LineItemColumn.CreateAd].header,
      Cell: (cell: Cell<LineItemModel>) => <CreateAndEditAdCell cell={cell} cellType={CellType.create} />,
      width: resizingData.getData(115, TraffickingTableName.lineItems, 'CreateAd'),
      tooltipMessage: LineItemColumnMeta[LineItemColumn.CreateAd].tooltip,
      disableForExport: true,
    },
    {
      id: LineItemColumn.Country,
      accessor: (li) => [li],
      Cell: (cell: Cell<LineItemModel>) => <CountryCell lineItemList={cell.value} />,
      Header: LineItemColumnMeta[LineItemColumn.Country].header,
      width: resizingData.getData(115, TraffickingTableName.lineItems, 'Country'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.Country].tooltip,
      exportAccessor: (li) => getUniqueCountriesString([li]),
    },
    {
      id: LineItemColumn.DeliverableLineItemPackageName,
      accessor: (li) => li.orderLineItemDto?.packageName,
      Cell: (cell: Cell<LineItemModel>) => <span>{cell.row.original.orderLineItemDto?.packageName}</span>,
      Header: LineItemColumnMeta[LineItemColumn.DeliverableLineItemPackageName].header,
      width: resizingData.getData(300, TraffickingTableName.lineItems, 'DeliverableLineItemPackageName'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.DeliverableLineItemPackageName].tooltip,
    },
    {
      id: LineItemColumn.TrackingId,
      accessor: 'trackingId',
      Cell: (cell: Cell<LineItemModel>) => <span>{cell.row.original.trackingId}</span>,
      Header: LineItemColumnMeta[LineItemColumn.TrackingId].header,
      width: resizingData.getData(175, TraffickingTableName.lineItems, 'TrackingId'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.TrackingId].tooltip,
      exportAccessor: getTrackingId,
    },
    {
      id: LineItemColumn.ParentOmsLinkId,
      accessor: (li) => li.parentLineOmsLink?.id,
      Cell: (cell: Cell<LineItemModel>) => <span>{cell.row.original.parentLineOmsLink?.id}</span>,
      Header: LineItemColumnMeta[LineItemColumn.ParentOmsLinkId].header,
      width: resizingData.getData(185, TraffickingTableName.lineItems, 'ParentOmsLinkId'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.ParentOmsLinkId].tooltip,
    },
    {
      id: LineItemColumn.BillingCalendar,
      accessor: (li) => li.billingCalendar?.displayName,
      Cell: (cell: Cell<LineItemModel>) => <span>{cell.row.original.billingCalendar?.displayName}</span>,
      Header: LineItemColumnMeta[LineItemColumn.BillingCalendar].header,
      width: resizingData.getData(170, TraffickingTableName.lineItems, 'BillingCalendar'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.BillingCalendar].tooltip,
    },
    {
      id: LineItemColumn.Status,
      Header: LineItemColumnMeta[LineItemColumn.Status].header,
      accessor: (li) => li.status,
      isEditable: true,
      doNotLighten: true,
      Cell: LineItemStatusCell,
      width: resizingData.getData(140, TraffickingTableName.lineItems, 'Status'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.Status].tooltip,
      exportAccessor: (li) => li.subStatus?.displayName ?? li.status?.displayName ?? '',
    },
    {
      id: LineItemColumn.Review,
      accessor: (li) => li.review,
      Header: LineItemColumnMeta[LineItemColumn.Review].header,
      Cell: ReviewCell,
      width: resizingData.getData(150, TraffickingTableName.lineItems, 'Review'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.Review].tooltip,
      exportAccessor: (li) => li.review?.displayName || '',
    },
    {
      id: LineItemColumn.ScheduleStart,
      accessor: (li) => li.startDate,
      Header: (
        <DateHeaderLabel
          label={LineItemColumnMeta[LineItemColumn.ScheduleStart].header}
          subLabel={LineItemColumnMeta[LineItemColumn.ScheduleStart].subHeader}
          dataTestid="start-date-header"
        />
      ),
      headerString: LineItemColumnMeta[LineItemColumn.ScheduleStart].header,
      Cell: (cell: Cell<LineItemModel>) => (
        <span>{formatStartEndDateString({ value: cell.row.original.startDate })}</span>
      ),
      width: resizingData.getData(210, TraffickingTableName.lineItems, 'ScheduleStart'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.ScheduleStart].tooltip,
      exportAccessor: (li) => formatStartEndDateString({ value: li.startDate }) ?? '',
    },
    {
      id: LineItemColumn.ActualStartDate,
      accessor: (li) => li.metadata?.actualStartDate || null,
      Header: (
        <DateHeaderLabel
          label={LineItemColumnMeta[LineItemColumn.ActualStartDate].header}
          subLabel={LineItemColumnMeta[LineItemColumn.ActualStartDate].subHeader}
          dataTestid="actual-start-date-header"
        />
      ),
      headerString: LineItemColumnMeta[LineItemColumn.ActualStartDate].header,
      Cell: (cell: Cell<LineItemModel>) => (
        <span>
          {formatUTCToTimezone({
            value: cell.row.original.metadata?.actualStartDate ?? null,
            timezone: cell.row.original.schedule?.timezone,
          })}
        </span>
      ),
      width: resizingData.getData(210, TraffickingTableName.lineItems, 'ActualStartDate'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.ActualStartDate].tooltip,
      exportAccessor: (li) =>
        formatUTCToTimezone({ value: li.metadata?.actualStartDate ?? null, timezone: li.schedule?.timezone }) ?? '',
    },
    {
      id: LineItemColumn.ScheduleEnd,
      accessor: (li) => li.endDate,
      Header: (
        <DateHeaderLabel
          label={LineItemColumnMeta[LineItemColumn.ScheduleEnd].header}
          subLabel={LineItemColumnMeta[LineItemColumn.ScheduleEnd].subHeader}
          dataTestid="end-date-header"
        />
      ),
      headerString: LineItemColumnMeta[LineItemColumn.ScheduleEnd].header,
      Cell: (cell: Cell<LineItemModel>) => (
        <span>{formatStartEndDateString({ value: cell.row.original.endDate })}</span>
      ),
      width: resizingData.getData(210, TraffickingTableName.lineItems, 'ScheduleEnd'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.ScheduleEnd].tooltip,
      exportAccessor: (li) => formatStartEndDateString({ value: li.endDate }) ?? '',
    },
    {
      id: LineItemColumn.DisplayPublisherTarget,
      accessor: 'displayPublisherTarget',
      Header: LineItemColumnMeta[LineItemColumn.DisplayPublisherTarget].header,
      Cell: (cell: Cell<LineItemModel>) => (
        <span>{getPublisherTargetingList({ value: cell.row.original.displayPublisherTarget })}</span>
      ),
      width: resizingData.getData(230, TraffickingTableName.lineItems, 'DisplayPublisherTarget'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.DisplayPublisherTarget].tooltip,
      exportAccessor: getDisplayPublisherTarget,
    },
    {
      id: LineItemColumn.ScheduleTimezoneOffset,
      accessor: (li) => (li.schedule?.timezone ? getTimezoneWithGMT(li.schedule?.timezone) : ''),
      Header: LineItemColumnMeta[LineItemColumn.ScheduleTimezoneOffset].header,
      Cell: ({ value }: { value: Nullable<number> }) => <span>{value}</span>,
      width: resizingData.getData(230, TraffickingTableName.lineItems, 'ScheduleTimezoneOffset'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.ScheduleTimezoneOffset].tooltip,
    },
    {
      id: LineItemColumn.GeneralPacingStatus,
      accessor: (li) => li.pacingMetrics?.generalPacingStatus || null,
      Header: getLineItemsColumnMeta(LineItemColumn.GeneralPacingStatus).header,
      Cell: PacingStatusCell,
      width: resizingData.getData(245, TraffickingTableName.lineItems, 'GeneralPacingStatus'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: getLineItemsColumnMeta(LineItemColumn.GeneralPacingStatus).tooltip,
      exportAccessor: (li) => {
        const status = li.pacingMetrics?.generalPacingStatus;
        return status ? cellContentsByPacingStatus[status]?.title || '' : '';
      },
    },
    {
      id: LineItemColumn.Goal,
      accessor: (li) => {
        if (li.goal === null) return '';
        const formattedGoal = li.goal.toLocaleString();
        return li.pacingType.key === pacingTypes.SOV.key ? `${formattedGoal}%` : formattedGoal;
      },
      Cell: ({ value }: { value: Nullable<number> }) => <span>{value}</span>,
      Header: LineItemColumnMeta[LineItemColumn.Goal].header,
      width: resizingData.getData(200, TraffickingTableName.lineItems, 'Goal'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.Goal].tooltip,
      exportAccessor: (li) => li.goal?.toLocaleString() ?? '',
    },
    {
      id: LineItemColumn.MetricsCompletions,
      accessor: (li) => getMetricsRelatedColumnAccessor(li.metrics?.completions),
      Cell: ({ value }: { value: Nullable<string> }) => <span>{value ?? ABSENT_DISPLAY}</span>,
      Header: getLineItemsColumnMeta(LineItemColumn.MetricsCompletions).header,
      width: resizingData.getData(170, TraffickingTableName.lineItems, 'MetricsCompletions'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: getLineItemsColumnMeta(LineItemColumn.MetricsCompletions).tooltip,
    },
    ...(adsPacingStatusAlignment
      ? [
          {
            id: LineItemColumn.MetricsScheduledPercentPacing,
            accessor: (li: LineItemModel) => li.metrics?.scheduledPercentPacing,
            Header: LineItemColumnMeta[LineItemColumn.MetricsScheduledPercentPacing].header,
            Cell: ({ value }: { value: Nullable<number> }) => <span>{formatDecimalValuePercent(value, '')}</span>,
            width: resizingData.getData(180, TraffickingTableName.lineItems, 'MetricsScheduledPercentPacing'),
            minWidth: DEFAULT_COLUMN_MIN_WIDTH,
            tooltipMessage: LineItemColumnMeta[LineItemColumn.MetricsScheduledPercentPacing].tooltip,
            isColumnHiddenByDefault: adsPacingStatusAlignmentDefaultHide,
            exportAccessor: (li: LineItemModel) => formatDecimalValuePercent(li.metrics?.scheduledPercentPacing, ''),
          },
        ]
      : []),
    {
      id: LineItemColumn.MetricsPercentPacing,
      accessor: (li) => getMetricsPercentRelatedColumnAccessor(li.metrics?.percentPacing),
      Header: getLineItemsColumnMeta(LineItemColumn.MetricsPercentPacing).header,
      Cell: ({ value }: { value: Nullable<number> }) => (
        <span>{numberToPercentage({ value: value }) ?? ABSENT_DISPLAY}</span>
      ),
      width: resizingData.getData(150, TraffickingTableName.lineItems, 'MetricsPercentPacing'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: getLineItemsColumnMeta(LineItemColumn.MetricsPercentPacing).tooltip,
      exportAccessor: (li) => getPercentageValueForExport(li.metrics?.percentPacing),
    },
    {
      id: LineItemColumn.MetricsPercentExpected,
      accessor: (li) => getMetricsPercentRelatedColumnAccessor(li.metrics?.percentExpected),
      Header: getLineItemsColumnMeta(LineItemColumn.MetricsPercentExpected).header,
      Cell: ({ value }: { value: Nullable<number> }) => (
        <span>{numberToPercentage({ value: value }) ?? ABSENT_DISPLAY}</span>
      ),
      width: resizingData.getData(165, TraffickingTableName.lineItems, 'MetricsPercentExpected'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: getLineItemsColumnMeta(LineItemColumn.MetricsPercentExpected).tooltip,
      exportAccessor: (li) => getPercentageValueForExport(li.metrics?.percentExpected),
      isColumnHiddenByDefault: adsPacingStatusAlignmentDefaultHide,
    },
    {
      id: LineItemColumn.MetricsPercentCompletions,
      accessor: (li) =>
        adsPacingStatusAlignment
          ? li.metrics?.percentCompletions
          : getMetricsPercentRelatedColumnAccessor(li.metrics?.percentCompletions),
      Header: getLineItemsColumnMeta(LineItemColumn.MetricsPercentCompletions).header,
      Cell: ({ value }: { value: Nullable<number> }) => (
        <span>
          {adsPacingStatusAlignment
            ? formatDecimalValuePercent(value)
            : numberToPercentage({ value }) ?? ABSENT_DISPLAY}
        </span>
      ),
      width: resizingData.getData(165, TraffickingTableName.lineItems, 'MetricsPercentCompletions'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: getLineItemsColumnMeta(LineItemColumn.MetricsPercentCompletions).tooltip,
      exportAccessor: (li) => {
        if (adsPacingStatusAlignment) {
          return formatDecimalValuePercent(li.metrics?.percentCompletions);
        }
        return getPercentageValueForExport(li.metrics?.percentCompletions);
      },
    },
    {
      id: LineItemColumn.MetricsImpressions,
      accessor: (li) => getMetricsRelatedColumnAccessor(li.metrics?.impressions),
      Cell: ({ value }: { value: Nullable<string> }) => <span>{value ?? ABSENT_DISPLAY}</span>,
      Header: getLineItemsColumnMeta(LineItemColumn.MetricsImpressions).header,
      width: resizingData.getData(165, TraffickingTableName.lineItems, 'MetricsImpressions'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: getLineItemsColumnMeta(LineItemColumn.MetricsImpressions).tooltip,
    },
    {
      id: LineItemColumn.BillableEvent,
      accessor: (li) => li.billableEvent,
      Cell: ({ value }: { value: Nullable<string> }) => <span>{value}</span>,
      Header: LineItemColumnMeta[LineItemColumn.BillableEvent].header,
      width: resizingData.getData(200, TraffickingTableName.lineItems, 'BillableEvent'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.BillableEvent].tooltip,
    },
    {
      id: LineItemColumn.PacingType,
      accessor: (li) => ({ pacingType: li.pacingType, tierName: li.tier.name.key }),
      Header: LineItemColumnMeta[LineItemColumn.PacingType].header,
      Cell: PacingTypeCell,
      isEditable: true,
      width: resizingData.getData(180, TraffickingTableName.lineItems, 'PacingType'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.PacingType].tooltip,
      exportAccessor: (li) => li.pacingType?.displayName || '',
    },
    {
      id: LineItemColumn.Tier,
      accessor: (li) => ({ pacingType: li.pacingType, tier: li.tier }),
      Header: LineItemColumnMeta[LineItemColumn.Tier].header,
      Cell: TierNameCell,
      isEditable: true,
      width: resizingData.getData(230, TraffickingTableName.lineItems, 'Tier'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.Tier].tooltip,
      exportAccessor: (li) => li.tier?.name?.displayName || '',
    },
    {
      id: LineItemColumn.YodaThreshold,
      accessor: (li) => getLineItemThreshold(li?.pacingMetrics?.yodaThreshold ?? null),
      Cell: ({ value }: { value: Nullable<number> }) => <span>{value}</span>,
      Header: LineItemColumnMeta[LineItemColumn.YodaThreshold].header,
      width: resizingData.getData(230, TraffickingTableName.lineItems, 'YodaThreshold'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.YodaThreshold].tooltip,
    },
    ...(disabledColumns?.includes(LineItemColumn.YodaPacingStatus)
      ? []
      : [
          {
            id: LineItemColumn.YodaPacingStatus,
            accessor: (li: LineItemModel) => li?.pacingMetrics?.yodaPacingStatus,
            Header: LineItemColumnMeta[LineItemColumn.YodaPacingStatus].header,
            Cell: YodaPacingStatusCell,
            width: resizingData.getData(230, TraffickingTableName.lineItems, 'YodaPacingStatus'),
            minWidth: DEFAULT_COLUMN_MIN_WIDTH,
            tooltipMessage: LineItemColumnMeta[LineItemColumn.YodaPacingStatus].tooltip,
          },
        ]),
    {
      id: LineItemColumn.PriorityName,
      accessor: (li) => li.priority,
      Header: LineItemColumnMeta[LineItemColumn.PriorityName].header,
      Cell: PriorityNameCell,
      isEditable: true,
      doNotLighten: true,
      width: resizingData.getData(230, TraffickingTableName.lineItems, 'PriorityName'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.PriorityName].tooltip,
      exportAccessor: (li) => li.priority?.name || '',
    },
    {
      id: LineItemColumn.PriorityValue,
      accessor: (li) => li.priorityValue,
      Cell: ({ value }: { value: Nullable<number> }) => <span>{value}</span>,
      Header: LineItemColumnMeta[LineItemColumn.PriorityValue].header,
      width: resizingData.getData(150, TraffickingTableName.lineItems, 'PriorityValue'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.PriorityValue].tooltip,
      exportAccessor: (li) => li.priorityValue?.toString() ?? '',
    },
    {
      id: LineItemColumn.AdProduct,
      accessor: (li) => li.adProduct.displayName,
      Cell: ({ value }: { value: Nullable<number> }) => <span>{value}</span>,
      Header: LineItemColumnMeta[LineItemColumn.AdProduct].header,
      width: resizingData.getData(250, TraffickingTableName.lineItems, 'AdProduct'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.AdProduct].tooltip,
    },
    {
      id: LineItemColumn.FrequencyCapList,
      accessor: (li) => li,
      isEditable: true,
      doNotLighten: true,
      Header: LineItemColumnMeta[LineItemColumn.FrequencyCapList].header,
      Cell: LineItemFrequencyCapCell,
      width: resizingData.getData(160, TraffickingTableName.lineItems, 'frequencyCapList'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.FrequencyCapList].tooltip,
      exportAccessor: getFrequencyCapList,
    },
    {
      id: LineItemColumn.BrandCapping,
      accessor: ({ brandCapping }) => brandCapping || '',
      Cell: ({ value }: { value: Nullable<number> }) => <span>{value}</span>,
      Header: LineItemColumnMeta[LineItemColumn.BrandCapping].header,
      width: resizingData.getData(115, TraffickingTableName.lineItems, 'brandCapping'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.BrandCapping].tooltip,
      // NO_COUNT_CAP should display as empty in export
      exportAccessor: (li) => (li.brandCapping && li.brandCapping !== 'NO_COUNT_CAP' ? li.brandCapping : ''),
    },
    {
      id: LineItemColumn.AutomateBuffer,
      accessor: (li) => li.automateBuffer,
      exportAccessor: (li) => boolToYes({ value: li.automateBuffer ?? null, isNoShown: true }) ?? '',
      isEditable: true,
      doNotLighten: true,
      Header: LineItemColumnMeta[LineItemColumn.AutomateBuffer].header,
      Cell: ({ row }: Cell<LineItemModel>) => (
        <BufferOptInCell
          automateBuffer={row.original.automateBuffer ?? null}
          lineItemId={row.original.id}
          pacingType={row.original.pacingType}
          ssp={row.original.ssp ?? null}
          billableThirdParty={row.original.billableThirdParty ?? null}
        />
      ),
      width: resizingData.getData(130, TraffickingTableName.lineItems, 'AutomateBuffer'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.AutomateBuffer].tooltip,
    },
    {
      id: LineItemColumn.DeliveryBufferPercent,
      accessor: (li) => (li.automateBuffer !== false ? li.automatedDeliveryBufferPercent : li.deliveryBufferPercent),
      isEditable: true,
      doNotLighten: true,
      Header: LineItemColumnMeta[LineItemColumn.DeliveryBufferPercent].header,
      Cell: ({ row }: Cell<LineItemModel>) => (
        <BufferCell
          automateBuffer={row.original.automateBuffer ?? null}
          automatedDeliveryBufferPercent={row.original.automatedDeliveryBufferPercent ?? null}
          deliveryBufferPercent={row.original.deliveryBufferPercent}
          isSOV={row.original.pacingType === pacingTypes.SOV}
          id={row.original.id}
        />
      ),
      width: resizingData.getData(180, TraffickingTableName.lineItems, 'DeliveryBufferPercent'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.DeliveryBufferPercent].tooltip,
      exportColumns: [
        {
          header: 'Automated Buffer',
          accessor: (li) => getPercentageValueToShow(li.automatedDeliveryBufferPercent),
        },
        {
          header: 'User-Applied Buffer',
          accessor: (li) => getPercentageValueToShow(li.deliveryBufferPercent),
        },
      ],
    },
    {
      id: LineItemColumn.FrontLoadPercent,
      accessor: (li) => li.frontLoadPercent,
      isEditable: true,
      doNotLighten: true,
      Header: LineItemColumnMeta[LineItemColumn.FrontLoadPercent].header,
      Cell: (cell: Cell<LineItemModel>) => (
        <PercentCell
          value={cell.value}
          id={getIdFromTableCell(cell)}
          name="frontLoadPercent"
          lowerLimit={0}
          upperLimit={400}
          showEmptyCell={
            cell.row.original.pacingType === pacingTypes.SOV || cell.row.original.pacingType === pacingTypes.AFAP
          }
        />
      ),
      width: resizingData.getData(150, TraffickingTableName.lineItems, 'FrontLoadPercent'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.FrontLoadPercent].tooltip,
      exportAccessor: (li) => getPercentageValueToShow(li.frontLoadPercent),
    },
    {
      id: LineItemColumn.DeliveryForensics,
      Header: LineItemColumnMeta[LineItemColumn.DeliveryForensics].header,
      Cell: DeliveryForensicsCell,
      isEditable: true,
      width: resizingData.getData(80, TraffickingTableName.lineItems, 'deliveryForensics'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      className: 'df-cell',
      tooltipMessage: LineItemColumnMeta[LineItemColumn.DeliveryForensics].tooltip,
      exportAccessor: (li) => `${DELIVERY_FORENSICS_LINK}?line-item-id=${li.id}`,
    },
    {
      id: LineItemColumn.AccessorId,
      accessor: 'id',
      Cell: ({ value }: { value: Nullable<number> }) => <span>{value}</span>,
      Header: LineItemColumnMeta[LineItemColumn.AccessorId].header,
      width: resizingData.getData(330, TraffickingTableName.lineItems, 'accessorId'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.AccessorId].tooltip,
    },
    {
      id: LineItemColumn.MetricsRemainingExpectedCompletionsAcc,
      accessor: (li) =>
        getMetricsRelatedColumnAccessor(getLineItemRemainingExpectedCompletionsAcc(li, adsPacingStatusAlignment)),
      Cell: ({ value }: { value: Nullable<string> }) => <span>{value ?? ABSENT_DISPLAY}</span>,
      Header: getLineItemsColumnMeta(LineItemColumn.MetricsRemainingExpectedCompletionsAcc).header,
      width: resizingData.getData(260, TraffickingTableName.lineItems, 'MetricsRemainingExpectedCompletionsAcc'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: getLineItemsColumnMeta(LineItemColumn.MetricsRemainingExpectedCompletionsAcc).tooltip,
      exportAccessor: (li) =>
        getMetricsRelatedColumnAccessor(getLineItemRemainingExpectedCompletionsAcc(li, adsPacingStatusAlignment)) ??
        ABSENT_EXPORT,
    },
    {
      id: LineItemColumn.MetricsCompletionsAtRisk,
      accessor: (li) => getMetricsRelatedColumnAccessor(li?.metrics?.completionsAtRisk),
      Cell: ({ value }: { value: Nullable<string> }) => <span>{value ?? ABSENT_DISPLAY}</span>,
      Header: getLineItemsColumnMeta(LineItemColumn.MetricsCompletionsAtRisk).header,
      width: resizingData.getData(220, TraffickingTableName.lineItems, 'MetricsCompletionsAtRisk'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: getLineItemsColumnMeta(LineItemColumn.MetricsCompletionsAtRisk).tooltip,
      isColumnHiddenByDefault: adsPacingStatusAlignmentDefaultHide,
    },
    {
      id: LineItemColumn.Revenue,
      accessor: 'revenue',
      Header: getLineItemsColumnMeta(LineItemColumn.Revenue).header,
      Cell: ({ value, row }: Cell<LineItemModel>) => (
        <span>{numberToCurrencyAmount(value, row.original.unitCostDto?.currency) ?? ABSENT_DISPLAY}</span>
      ),
      width: resizingData.getData(220, TraffickingTableName.lineItems, 'Revenue'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      className: 'price-cell',
      tooltipMessage: getLineItemsColumnMeta(LineItemColumn.Revenue).tooltip,
      exportAccessor: (li) => getCurrencyValueToShow(li.revenue, li.unitCostDto?.currency),
    },
    {
      id: LineItemColumn.MetricsRevenueAtRisk,
      // KATANA-1745: nullish, not falsy, so a 0.0 renders $0.00 and only a missing value is blank.
      accessor: (li) => li.metrics?.revenueAtRisk ?? null,
      Header: getLineItemsColumnMeta(LineItemColumn.MetricsRevenueAtRisk).header,
      Cell: ({ value, row }: Cell<LineItemModel>) => (
        <span>{numberToCurrencyAmount(value, row.original.unitCostDto?.currency) ?? ABSENT_DISPLAY}</span>
      ),
      width: resizingData.getData(260, TraffickingTableName.lineItems, 'MetricsRevenueAtRisk'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      className: 'price-cell',
      tooltipMessage: getLineItemsColumnMeta(LineItemColumn.MetricsRevenueAtRisk).tooltip,
      exportAccessor: (li) =>
        numberToCurrencyAmount(li.metrics?.revenueAtRisk, li.unitCostDto?.currency) ?? ABSENT_EXPORT,
      isColumnHiddenByDefault: adsPacingStatusAlignmentDefaultHide,
    },
    {
      id: LineItemColumn.BillableThirdParty,
      accessor: (li) => li.billableThirdParty?.displayName,
      Cell: ({ value }: { value: Nullable<number> }) => <span>{value}</span>,
      Header: LineItemColumnMeta[LineItemColumn.BillableThirdParty].header,
      width: resizingData.getData(225, TraffickingTableName.lineItems, 'BillableThirdParty'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.BillableThirdParty].tooltip,
    },
    {
      id: LineItemColumn.UnitCost,
      accessor: ({ unitCostDto }) => unitCostDto?.micros ?? null,
      Header: LineItemColumnMeta[LineItemColumn.UnitCost].header,
      Cell: ({ value, row }: Cell<LineItemModel>) => (
        <span>{formatMicroCurrencyAmount(value, row.original.unitCostDto?.currency) ?? ABSENT_DISPLAY}</span>
      ),
      width: resizingData.getData(140, TraffickingTableName.lineItems, 'UnitCost'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      className: 'price-cell',
      tooltipMessage: LineItemColumnMeta[LineItemColumn.UnitCost].tooltip,
      exportAccessor: (li) =>
        formatMicroCurrencyAmount(li.unitCostDto?.micros, li.unitCostDto?.currency) ?? ABSENT_EXPORT,
    },
    ...(disabledColumns?.includes(LineItemColumn.MetricsUnderPacingRevenue)
      ? []
      : [
          {
            id: LineItemColumn.MetricsUnderPacingRevenue,
            accessor: (li: LineItemModel) => li.metrics?.underPacingRevenue ?? null,
            Header: LineItemColumnMeta[LineItemColumn.MetricsUnderPacingRevenue].header,
            Cell: ({ value, row }: Cell<LineItemModel>) => (
              <span>{numberToCurrencyAmount(value, row.original.unitCostDto?.currency) ?? ABSENT_DISPLAY}</span>
            ),
            width: resizingData.getData(245, TraffickingTableName.lineItems, 'MetricsUnderPacingRevenue'),
            minWidth: DEFAULT_COLUMN_MIN_WIDTH,
            className: 'price-cell',
            tooltipMessage: LineItemColumnMeta[LineItemColumn.MetricsUnderPacingRevenue].tooltip,
            exportAccessor: (li: LineItemModel) =>
              getCurrencyValueToShow(li.metrics?.underPacingRevenue, li.unitCostDto?.currency),
          },
        ]),
    {
      id: LineItemColumn.LineOmsLinkId,
      accessor: (li) => li.lineOmsLink?.id,
      Cell: ({ value }: { value: Nullable<number> }) => <span>{value}</span>,
      Header: LineItemColumnMeta[LineItemColumn.LineOmsLinkId].header,
      width: resizingData.getData(200, TraffickingTableName.lineItems, 'LineOmsLinkId'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.LineOmsLinkId].tooltip,
    },
    {
      id: LineItemColumn.CreatedAt,
      accessor: 'createdAt',
      Header: LineItemColumnMeta[LineItemColumn.CreatedAt].header,
      Cell: ({ value }: { value: Nullable<string> }) => <span>{stringToDateTimeString({ value })}</span>,
      width: resizingData.getData(190, TraffickingTableName.lineItems, 'CreatedAt'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.CreatedAt].tooltip,
      exportAccessor: getCreatedAt,
    },
    {
      id: LineItemColumn.UpdatedAt,
      accessor: 'updatedAt',
      Header: LineItemColumnMeta[LineItemColumn.UpdatedAt].header,
      Cell: ({ value }: { value: Nullable<string> }) => <span>{stringToDateTimeString({ value })}</span>,
      width: resizingData.getData(190, TraffickingTableName.lineItems, 'UpdatedAt'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.UpdatedAt].tooltip,
      exportAccessor: getUpdatedAt,
    },
    {
      id: LineItemColumn.CreativeMaxSeconds,
      accessor: 'creativeMaxSeconds',
      Cell: ({ value }: { value: Nullable<number> }) => <span>{value}</span>,
      Header: LineItemColumnMeta[LineItemColumn.CreativeMaxSeconds].header,
      width: resizingData.getData(175, TraffickingTableName.lineItems, 'CreativeMaxSeconds'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.CreativeMaxSeconds].tooltip,
      exportAccessor: (li) => li.creativeMaxSeconds?.toString() ?? '',
    },
    {
      id: LineItemColumn.CampaignName,
      accessor: (li) => li?.campaign?.name,
      Cell: ({ value }: { value: Nullable<number> }) => <span>{value}</span>,
      Header: LineItemColumnMeta[LineItemColumn.CampaignName].header,
      width: resizingData.getData(525, TraffickingTableName.lineItems, 'CampaignName'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.CampaignName].tooltip,
    },
    {
      id: LineItemColumn.DeliveryGoal,
      accessor: (li) => {
        const deliveryGoal = li?.metrics?.adjustedDeliveryGoal;

        if (deliveryGoal === null || deliveryGoal === undefined) {
          return '';
        }

        return li.pacingType.key === pacingTypes.SOV.key
          ? `${deliveryGoal.toLocaleString()}%`
          : `${deliveryGoal.toLocaleString()}`;
      },
      Cell: ({ value }: { value: Nullable<number> }) => <span>{value}</span>,
      Header: getLineItemsColumnMeta(LineItemColumn.DeliveryGoal).header,
      width: resizingData.getData(175, TraffickingTableName.lineItems, 'DeliveryGoal'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: getLineItemsColumnMeta(LineItemColumn.DeliveryGoal).tooltip,
      exportAccessor: (li) => li?.metrics?.adjustedDeliveryGoal?.toLocaleString() ?? '',
    },
    {
      id: LineItemColumn.IsCoppaOrCaru,
      accessor: 'isCoppaOrCaru',
      Header: LineItemColumnMeta[LineItemColumn.IsCoppaOrCaru].header,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.IsCoppaOrCaru].tooltip,
      ...commonBooleanCellConfig('IsCoppaOrCaru'),
      exportAccessor: (li) => boolToYes({ value: li.isCoppaOrCaru }) ?? '',
    },
    {
      id: LineItemColumn.IsAddedValue,
      accessor: 'isAddedValue',
      Header: LineItemColumnMeta[LineItemColumn.IsAddedValue].header,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.IsAddedValue].tooltip,
      ...commonBooleanCellConfig('IsAddedValue'),
      exportAccessor: (li) => boolToYes({ value: li.isAddedValue }) ?? '',
    },
    {
      id: LineItemColumn.IsMakegood,
      accessor: 'isMakegood',
      Header: LineItemColumnMeta[LineItemColumn.IsMakegood].header,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.IsMakegood].tooltip,
      ...commonBooleanCellConfig('IsMakegood'),
      exportAccessor: (li) => boolToYes({ value: li.isMakegood }) ?? '',
    },
    {
      id: LineItemColumn.IsFiller,
      accessor: 'isFiller',
      Header: LineItemColumnMeta[LineItemColumn.IsFiller].header,
      isColumnHiddenByDefault: true,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.IsFiller].tooltip,
      ...commonBooleanCellConfig('isFiller'),
      exportAccessor: (li) => boolToYes({ value: li.isFiller }) ?? '',
    },
    {
      id: LineItemColumn.DeliverableLineItemBuyerName,
      accessor: (li) => li.orderLineItemDto?.buyerName,
      Cell: ({ value }: { value: Nullable<number> }) => <span>{value}</span>,
      Header: LineItemColumnMeta[LineItemColumn.DeliverableLineItemBuyerName].header,
      width: resizingData.getData(175, TraffickingTableName.lineItems, 'DeliverableLineItemBuyerName'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.DeliverableLineItemBuyerName].tooltip,
    },
    {
      id: LineItemColumn.DeliverableLineItemBuyerId,
      accessor: (li) => li.orderLineItemDto?.buyerId,
      Cell: ({ value }: { value: Nullable<number> }) => <span>{value}</span>,
      Header: LineItemColumnMeta[LineItemColumn.DeliverableLineItemBuyerId].header,
      width: resizingData.getData(175, TraffickingTableName.lineItems, 'DeliverableLineItemBuyerId'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.DeliverableLineItemBuyerId].tooltip,
    },
    {
      id: LineItemColumn.DspName,
      accessor: (li) => li?.dealMetadata?.dspName ?? '',
      Cell: ({ value }: { value: Nullable<number> }) => <span>{value}</span>,
      Header: LineItemColumnMeta[LineItemColumn.DspName].header,
      width: resizingData.getData(175, TraffickingTableName.lineItems, 'DspName'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.DspName].tooltip,
    },
    {
      id: LineItemColumn.DealId,
      accessor: (li) => li?.dealMetadata?.dealId ?? '',
      Cell: ({ row }: Cell<LineItemModel>) => (
        <DealIdCell
          dealId={row.original.deal?.dealId}
          ssp={row.original.ssp}
          metadataDealId={row.original.dealMetadata?.dealId}
        />
      ),
      Header: LineItemColumnMeta[LineItemColumn.DealId].header,
      width: resizingData.getData(175, TraffickingTableName.lineItems, 'DealId'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.DealId].tooltip,
    },
    {
      id: LineItemColumn.CampaignTraffickerEmail,
      accessor: ({ campaign }) => getAccountRelatedColumnAccessor(campaign?.traffickerEmail),
      Cell: ({ value }: { value: Nullable<number> }) => <span>{value}</span>,
      Header: LineItemColumnMeta[LineItemColumn.CampaignTraffickerEmail].header,
      width: resizingData.getData(220, TraffickingTableName.lineItems, 'CampaignTraffickerEmail'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.CampaignTraffickerEmail].tooltip,
    },
    {
      id: LineItemColumn.CampaignOrderSalespersonEmail,
      accessor: ({ campaign }) => getAccountRelatedColumnAccessor(campaign?.order?.salespersonEmail),
      Cell: ({ value }: { value: Nullable<number> }) => <span>{value}</span>,
      Header: LineItemColumnMeta[LineItemColumn.CampaignOrderSalespersonEmail].header,
      width: resizingData.getData(220, TraffickingTableName.lineItems, 'CampaignOrderSalespersonEmail'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.CampaignOrderSalespersonEmail].tooltip,
    },
    {
      id: LineItemColumn.Currency,
      accessor: ({ unitCostDto }) => unitCostDto?.currency || '',
      Cell: ({ value }: { value: Nullable<number> }) => <span>{value}</span>,
      Header: LineItemColumnMeta[LineItemColumn.Currency].header,
      width: resizingData.getData(200, TraffickingTableName.lineItems, 'Currency'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.Currency].tooltip,
    },
    {
      id: LineItemColumn.CustomPacingEligible,
      Header: LineItemColumnMeta[LineItemColumn.CustomPacingEligible].header,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.CustomPacingEligible].tooltip,
      accessor: (li) => boolToYes({ value: !!li.cpEligible, isNoShown: true }),
      width: resizingData.getData(190, TraffickingTableName.lineItems, 'CustomPacingEligible'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      exportAccessor: (li) => boolToYes({ value: li.cpEligible ?? null }) ?? '',
    },
    {
      id: LineItemColumn.CustomPacingRequested,
      Cell: ({ row }: Cell<LineItemModel>) => (
        <CustomPacingRequestedCell lineItemId={row.original.id} customPacingRequested={row.original.cpRequested} />
      ),
      Header: LineItemColumnMeta[LineItemColumn.CustomPacingRequested].header,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.CustomPacingRequested].tooltip,
      width: resizingData.getData(190, TraffickingTableName.lineItems, 'CustomPacingRequested'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      exportAccessor: (li) => li.cpRequested?.displayName || '',
      isEditable: true,
    },
    {
      id: LineItemColumn.CustomPacingApplied,
      Cell: CustomPacingAppliedCell,
      accessor: 'customPacingApplied',
      Header: LineItemColumnMeta[LineItemColumn.CustomPacingApplied].header,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.CustomPacingApplied].tooltip,
      width: resizingData.getData(200, TraffickingTableName.lineItems, 'CustomPacingApplied'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      exportAccessor: (li) => boolToYes({ value: li.customPacingApplied ?? null, isNoShown: true }) ?? '',
    },
    {
      id: LineItemColumn.CostMethod,
      accessor: (li) => li?.costMethod?.displayName || '',
      Header: LineItemColumnMeta[LineItemColumn.CostMethod].header,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.CostMethod].tooltip,
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
    },
    {
      id: LineItemColumn.SSP,
      accessor: (li) => li.ssp,
      Cell: ({ value }: { value: Nullable<LineItemSsp> }) => <span>{_capitalize(value ?? '')}</span>,
      Header: LineItemColumnMeta[LineItemColumn.SSP].header,
      width: resizingData.getData(175, TraffickingTableName.lineItems, 'SSP'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.SSP].tooltip,
      exportAccessor: (li) => _capitalize(li.ssp ?? '') || '',
    },
    {
      id: LineItemColumn.DistributionPartnerList,
      accessor: (li) => li.distributionPartnerList,
      Header: LineItemColumnMeta[LineItemColumn.DistributionPartnerList].header,
      Cell: (cell: Cell<LineItemModel>) => (
        <span>{getDistributionPartnerList({ value: cell.row.original.distributionPartnerList })}</span>
      ),
      width: resizingData.getData(175, TraffickingTableName.lineItems, 'DistributionPartner'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.DistributionPartnerList].tooltip,
      exportAccessor: getDistributionPartners,
    },
  ];

  if (envConfig.REACT_APP_FEATURE_ENABLE_CREATE_SEQUENCE === 'true') {
    columns.push({
      id: LineItemColumn.Sequence,
      accessor: (li) => boolToYes({ value: !!li.lineItemSequenceId, isNoShown: true }),
      Cell: ({ value }: { value: Nullable<number> }) => <span>{value}</span>,
      Header: LineItemColumnMeta[LineItemColumn.Sequence].header,
      width: resizingData.getData(175, TraffickingTableName.lineItems, 'Sequence'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: LineItemColumnMeta[LineItemColumn.Sequence].tooltip,
    });
  }

  return columns;
};

export default getLineItemsColumns;
